import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Drawer,
    GlobalStyles,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import PrintRounded from '@mui/icons-material/PrintRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import Screen, { type ScreenProps } from 'ui/layouts/Screen';
import {
    directApiEndpoints,
    errorMessages,
    listFilterSyntax,
    listPaginationSyntax,
    notificationFields,
    notificationHashFormula,
    notificationPayloadExample,
    notificationReceiverExample,
    notificationRetrySchedule,
    processorEndpoints,
    webhookDecryptExample,
    webhookPaReqExample,
    webhookRedirectFields,
    whitelistEndpoints,
    whitelistTemplateWithoutSddColumns
} from './content';
import {
    CodeBlock,
    DOCUMENTATION_HEADER_HEIGHT_VAR,
    DOCUMENTATION_NAV_TOP,
    DocGroup,
    DocSection,
    DocumentationSearchContext,
    EndpointSection,
    ParametersTable,
    SimpleTable,
    SubHeading
} from './components';
import DocumentationNav, { groupSectionIds } from './DocumentationNav';
import TransactionPlayground from './TransactionPlayground';
import usePotentialPaymentMethods, { type PotentialPaymentMethod } from './usePotentialPaymentMethods';
import useDocumentationSearch, { SEARCH_HIGHLIGHT_NAME } from './useDocumentationSearch';

const SEARCH_DEBOUNCE_MS = 150;

const Code = ({ children }: { readonly children: string }) => <Box component='code'>{children}</Box>;

const DocumentationContent = memo(({ paymentMethods, paymentMethodsLoading, paymentMethodsError }: {
    readonly paymentMethods: readonly PotentialPaymentMethod[];
    readonly paymentMethodsLoading: boolean;
    readonly paymentMethodsError: string | null;
}) => (
    <>
        <DocSection id='overview' divider={false} sx={{ pt: 0 }}>
            <Typography variant='h3' sx={{ mb: 2 }}>
                Integrate with Macropay
            </Typography>
            <Typography variant='h6' color='text.secondary' sx={{ maxWidth: 760 }}>
                Use the Macropay HTTP APIs to accept payments, follow transaction
                statuses, receive notifications and access merchant reporting data.
            </Typography>
        </DocSection>

        <DocSection id='environments'>
            <Typography variant='h4' gutterBottom>Environments</Typography>
            <Typography color='text.secondary' paragraph>
                All API communication uses HTTPS and UTF-8. TLS 1.2 or later is required.
            </Typography>
            <Stack spacing={1}>
                <CodeBlock title='Test base URL'>https://testapi.payments-gateway.co/v1</CodeBlock>
                <CodeBlock title='Live base URL'>https://api.payments-gateway.co/v1</CodeBlock>
            </Stack>
        </DocSection>

        <DocSection id='authentication'>
            <Typography variant='h4' gutterBottom>Authentication</Typography>
            <Typography color='text.secondary' paragraph>
                Your API ID and API GUID are available in Profile → Technical Information.
                Keep them secret and use the credentials that belong to the selected environment.
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Alert severity='info'>
                    <strong>Transaction and transaction status:</strong> send
                    <Box component='code' sx={{ mx: 0.5 }}>api_id</Box> and
                    <Box component='code' sx={{ mx: 0.5 }}>api_guid</Box> in the request body,
                    or send them as request headers
                    <Box component='code' sx={{ mx: 0.5 }}>api_id: &lt;API ID&gt;</Box> and
                    <Box component='code' sx={{ mx: 0.5 }}>api_secret: &lt;API GUID&gt;</Box>.
                    Send <Code>Accept: application/json</Code> in the request
                    headers. The body supports
                    <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/json</Box>
                    or
                    <Box component='code' sx={{ ml: 0.5 }}>Content-Type: application/x-www-form-urlencoded</Box>.
                </Alert>
                <Alert severity='info'>
                    <strong>Payment methods and consumer endpoints:</strong> send the following
                    request headers: <Box component='code' sx={{ mx: 0.5 }}>auth-id: &lt;API ID&gt;</Box>,
                    <Box component='code' sx={{ mx: 0.5 }}>auth-secret: &lt;API GUID&gt;</Box> and
                    <Box component='code' sx={{ mx: 0.5 }}>Accept: application/json</Box>.
                    For JSON request bodies use
                    <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/json</Box>;
                    for Consumer whitelist and KYC use
                    <Box component='code' sx={{ ml: 0.5 }}>Content-Type: multipart/form-data</Box>.
                </Alert>
            </Stack>
            <Alert severity='warning'>
                Never expose API credentials in public client-side code or commit them to source control.
            </Alert>
        </DocSection>

        <DocSection id='transaction-flow'>
            <Typography variant='h4' gutterBottom>Transaction flow</Typography>
            <Stack spacing={1.25} sx={{ mt: 2 }}>
                {[
                    'Your checkout creates a transaction.',
                    'Macropay selects the payment method using currency, country, channel, tag and optional specin fields.',
                    'Redirect the consumer to the one-time URL from the response.',
                    'The consumer completes the payment with the issuer.',
                    'Macropay sends status notifications to your notification URL.',
                    'Confirm the latest state with getTransactionStatus.'
                ].map((step, index) => (
                    <Stack key={step} direction='row' spacing={2} alignItems='center'>
                        <Chip label={index + 1} size='small' color='primary' />
                        <Typography>{step}</Typography>
                    </Stack>
                ))}
            </Stack>
        </DocSection>

        {processorEndpoints.map(endpoint => (
            <EndpointSection key={endpoint.id} endpoint={endpoint} />
        ))}

        <DocSection
            id='go-live-testing'
            sx={{
                my: 4,
                p: { xs: 2.5, md: 3 },
                border: 1,
                borderColor: 'warning.main',
                borderRadius: 2,
                bgcolor: 'warning.lighter'
            }}
        >
            <Typography variant='h4' gutterBottom>
                Before going live
            </Typography>
            <Alert severity='warning' sx={{ mb: 3 }}>
                When testing payment methods in production, use only generic test email
                addresses such as <Code>testswish@test.com</Code> or
                generic Yopmail test addresses. Do not use email addresses containing
                Macropay, merchant, brand or other entity domain names.
            </Alert>
            <Typography color='text.secondary' paragraph>
                Because production methods are being tested, Macropay needs an end-to-end
                recording before go-live sign-off. The recording should cover one brand
                that will be pushed live, from cashier through the payment method flow and
                redirect back to the merchant site. Ideally, record a successful end-to-end
                payment.
            </Typography>
            <Typography color='text.secondary'>
                Send the recording to Macropay by email for review.
            </Typography>
        </DocSection>

        <DocSection id='notifications'>
            <Typography variant='h4' gutterBottom>Notifications</Typography>
            <Typography color='text.secondary' paragraph>
                Notifications are mandatory. Macropay sends an HTTP(S) POST with
                <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/x-www-form-urlencoded</Box>
                to the <Code>notificationurl</Code> of the transaction as soon
                as its state changes.
            </Typography>

            <SubHeading>Payload</SubHeading>
            <ParametersTable parameters={notificationFields} />
            <Box sx={{ mt: 2 }}>
                <CodeBlock title='Example notification body (line breaks added for readability)'>
                    {notificationPayloadExample}
                </CodeBlock>
            </Box>

            <SubHeading>Status changes</SubHeading>
            <Alert severity='warning' sx={{ mb: 1.5 }}>
                Treat <Code>finalstatus</Code> as final only for
                <Box component='code' sx={{ ml: 0.5 }}>REFUNDED</Box>. Any other value describes
                the current status of the transaction, which can still change.
            </Alert>
            <Stack component='ul' spacing={0.5} sx={{ pl: 3, my: 0, color: 'text.secondary' }}>
                <li><Code>SUCCEEDED</Code> can follow <Code>FAILED</Code>.</li>
                <li>
                    <Code>REFUNDED</Code> or
                    <Box component='code' sx={{ mx: 0.5 }}>CHARGE_BACK</Box> can follow
                    <Box component='code' sx={{ ml: 0.5 }}>SUCCEEDED</Box>.
                </li>
                <li>
                    <Code>SUCCEEDED</Code> can follow
                    <Box component='code' sx={{ ml: 0.5 }}>CHARGE_BACK</Box>. This can repeat,
                    although rarely more than once or twice.
                </li>
            </Stack>
            <Typography color='text.secondary' sx={{ mt: 1.5 }}>
                Process notifications idempotently: the same notification can arrive more than once.
            </Typography>

            <SubHeading>Verify the signature</SubHeading>
            <Typography color='text.secondary' paragraph>
                The notification secret is the <strong>Shared Key</strong> shown in
                Profile → Technical Information. Recompute the hash and compare it with
                <Box component='code' sx={{ ml: 0.5 }}>sha256hash</Box>. Verification is
                optional; you can confirm the status with
                <Box component='code' sx={{ mx: 0.5 }}>POST /v1/getTransactionStatus</Box>
                instead. If you verify the hash and it does not match, call
                getTransactionStatus before acting on the notification.
            </Typography>
            <Stack spacing={2}>
                <CodeBlock title='Signature formula'>{notificationHashFormula}</CodeBlock>
                <CodeBlock title='Notification receiver example (PHP)'>
                    {notificationReceiverExample}
                </CodeBlock>
            </Stack>

            <SubHeading>Response and retries</SubHeading>
            <Typography color='text.secondary' paragraph>
                Macropay treats a notification as delivered when your server returns an HTTP
                2xx status within 30 seconds of the connection being opened. Any other result
                is retried on the following schedule:
            </Typography>
            <SimpleTable
                headers={['Retry', 'Delay (minutes)', 'Minutes after first attempt']}
                rows={notificationRetrySchedule}
                sx={{ maxWidth: 520 }}
            />
            <Typography color='text.secondary' sx={{ mt: 1.5 }}>
                Every delivery attempt and the response your server returned are listed by the
                Notification history endpoint in the Reporting and Payout API.
            </Typography>
        </DocSection>

        <DocSection id='webhook'>
            <Typography variant='h4' gutterBottom>Webhook redirect</Typography>
            <Typography color='text.secondary' paragraph>
                After the consumer authorizes the payment, they may be redirected to the
                <Box component='code' sx={{ mx: 0.5 }}>merchantredirecturl</Box> of the
                transaction. This redirect is not guaranteed: the consumer can close the page
                after authorizing. Process transactions from notifications, use
                getTransactionStatus as a fallback when notifications fail, and use the
                redirect only to show the consumer a result page.
            </Typography>
            <Alert severity='info' sx={{ mb: 3 }}>
                The redirect is an HTTP POST, so the browser does not send your login cookie
                and a signed-in consumer is not recognised on that request. Process the
                redirect, then reload the page with an HTTP GET so the browser sends the
                cookie and the consumer stays signed in.
            </Alert>

            <Typography variant='h6' sx={{ mb: 1.5 }}>Redirect body</Typography>
            <ParametersTable parameters={webhookRedirectFields} />

            <SubHeading>Decrypt PaReq</SubHeading>
            <Typography color='text.secondary' paragraph>
                <Code>PaReq</Code> is encrypted with AES-256-CBC. Decrypt it with
                the <strong>CPKey</strong> and <strong>CPSecret</strong> shown in
                Profile → Technical Information. The decrypted content is
                intended for inspection and debugging; fetch the transaction details with
                getTransactionStatus if you want to process the transaction on redirect.
            </Typography>
            <Stack spacing={2}>
                <CodeBlock title='Decryption example (PHP)'>{webhookDecryptExample}</CodeBlock>
                <CodeBlock title='Decrypted PaReq example'>{webhookPaReqExample}</CodeBlock>
            </Stack>
        </DocSection>

        <DocSection id='live-payment-methods'>
            <Typography variant='h4' gutterBottom>Available payment methods</Typography>
            <Typography color='text.secondary' paragraph>
                Explore the payment methods available through Macropay. Each entry shows
                the exact integration tag together with the supported countries and currencies.
            </Typography>
            {paymentMethodsLoading && (
                <Stack direction='row' spacing={1.5} alignItems='center' sx={{ py: 2 }}>
                    <CircularProgress size={22} />
                    <Typography color='text.secondary'>Loading payment methods…</Typography>
                </Stack>
            )}
            {paymentMethodsError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                    {paymentMethodsError}
                </Alert>
            )}
            {!paymentMethodsLoading && !paymentMethodsError && !paymentMethods.length && (
                <Alert severity='info' sx={{ mb: 2 }}>
                    No payment methods are currently available.
                </Alert>
            )}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    gap: 2
                }}
            >
                {paymentMethods.map(method => (
                    <Paper key={method.tag} variant='outlined' sx={{ p: 2.5 }}>
                        <Typography variant='h6'>{method.name}</Typography>
                        <Typography variant='caption' color='text.secondary'>Tag</Typography>
                        <Typography component='div' sx={{ mb: 2 }}>
                            <Code>{method.tag}</Code>
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>Countries</Typography>
                        <Stack direction='row' gap={0.75} flexWrap='wrap' sx={{ mt: 0.5, mb: 1.5 }}>
                            {method.countries.map(value => (
                                <Chip key={value} label={value} size='small' />
                            ))}
                        </Stack>
                        <Typography variant='caption' color='text.secondary'>Currencies</Typography>
                        <Stack direction='row' gap={0.75} flexWrap='wrap' sx={{ mt: 0.5 }}>
                            {method.currencies.map(value => (
                                <Chip key={value} label={value} size='small' variant='outlined' />
                            ))}
                        </Stack>
                    </Paper>
                ))}
            </Box>
        </DocSection>

        <DocSection id='statuses'>
            <Typography variant='h4' gutterBottom>Statuses and funds statuses</Typography>
            <Typography color='text.secondary' paragraph>
                Treat notifications as state changes rather than a single immutable final result.
            </Typography>
            <Stack direction='row' gap={1} flexWrap='wrap'>
                {['PENDING', 'REJECT', 'FAILED', 'SUCCEEDED', 'CHARGE_BACK', 'REFUNDED'].map(status => (
                    <Chip key={status} label={status} />
                ))}
            </Stack>
            <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>Funds statuses</Typography>
            <Stack direction='row' gap={1} flexWrap='wrap'>
                {['NOT_EXPECTED', 'WAITING', 'MISSING', 'RECEIVED', 'SENT', 'REFUNDED'].map(status => (
                    <Chip key={status} label={status} variant='outlined' />
                ))}
            </Stack>
            <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>Settlement statuses</Typography>
            <Stack direction='row' gap={1} flexWrap='wrap'>
                {['SUCCEEDED', 'FUNDSMISSING', 'FUNDSRECEIVED', 'FUNDSREFUNDED', 'FUNDSSENT'].map(status => (
                    <Chip key={status} label={status} color='secondary' variant='outlined' />
                ))}
            </Stack>
        </DocSection>

        <DocSection id='errors'>
            <Typography variant='h4' gutterBottom>Error messages</Typography>
            <SimpleTable headers={['Message', 'Description']} rows={errorMessages} sx={{ mt: 2 }} />
        </DocSection>

        <DocGroup
            title='Whitelist imports'
            description='Import, inspect and download consumer whitelist data.'
            sectionIds={groupSectionIds('Whitelist imports')}
        >
            <DocSection
                id='whitelist-process'
                divider={false}
                sx={{
                    my: 3,
                    p: { xs: 2.5, md: 3 },
                    border: 2,
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    bgcolor: 'primary.lighter'
                }}
            >
                <Alert severity='warning' sx={{ mb: 3 }}>
                    <Typography variant='subtitle2'>Mandatory on Sandbox and Production</Typography>
                    The whitelist is mandatory in both the Sandbox and the Production
                    environments. Consumers who are not whitelisted are declined, including
                    while testing on Sandbox, so submit the whitelist before you start
                    testing deposits.
                </Alert>

                <Typography variant='h4' gutterBottom>Whitelist process</Typography>
                <Typography color='text.secondary' paragraph>
                    Consumer whitelists can be submitted manually through the Macropay
                    Backoffice or automatically through the API endpoints documented below.
                    API automation is recommended for recurring consumer lists because it
                    removes the need for repeated manual uploads.
                </Typography>

                <Alert severity='info' sx={{ my: 3 }}>
                    <Typography variant='subtitle2' gutterBottom>
                        Whitelisting criteria
                    </Typography>
                    By default, unless otherwise agreed in the commercial terms, a consumer
                    is eligible for whitelisting after completing at least four successful
                    deposits using payment methods not provided by Macropay.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, my: 3 }}>
                    <Paper variant='outlined' sx={{ p: 2.5 }}>
                        <Typography variant='h6' gutterBottom>Backoffice import</Typography>
                        <Typography color='text.secondary'>
                            Upload the whitelist file directly from the Merchant Backoffice
                            when a manual submission is appropriate.
                        </Typography>
                    </Paper>
                    <Paper variant='outlined' sx={{ p: 2.5 }}>
                        <Typography variant='h6' gutterBottom>API automation</Typography>
                        <Typography color='text.secondary'>
                            Configure your integration to submit recurring consumer lists
                            directly to Macropay, without manually uploading each file.
                        </Typography>
                    </Paper>
                </Box>

                <Alert severity='info' sx={{ mb: 3 }}>
                    All received whitelist files are processed at the beginning of every
                    full hour (HH:00). For example, a file received at 16:15 is processed
                    at 17:00. Consumers included in that file can deposit after processing
                    completes.
                </Alert>

                <Typography variant='h6' gutterBottom>
                    Why the whitelist must be submitted in advance
                </Typography>
                <Typography color='text.secondary' paragraph>
                    If a consumer attempts to deposit before being whitelisted, the
                    transaction is declined with the response <Code>User is not in ecosystem</Code>.
                    Retrying does not change the result. The same response is
                    returned until the consumer is included in a whitelist and the file
                    has been successfully processed.
                </Typography>
                <Typography color='text.secondary' paragraph>
                    Once processing is complete, the consumer should be able to complete
                    the deposit on the next attempt. Submit whitelist files early enough
                    to account for the next hourly processing window.
                </Typography>
                <Alert severity='warning' sx={{ mt: 3 }}>
                    The whitelist template column order must be kept exactly as provided by
                    Macropay. Headers are mandatory because the first CSV row is treated as
                    the header row and skipped during import. Optional columns must remain in
                    the file, but their values can be left empty. If the template column order
                    is not respected, the data is considered invalid.
                </Alert>
                <Typography color='text.secondary' sx={{ mt: 3 }} paragraph>
                    After a whitelist file is uploaded through the API, it can be reviewed in
                    the Backoffice under Consumers → Whitelist Import History.
                </Typography>
                <Stack spacing={1}>
                    <Typography color='text.secondary'>
                        <Box component='strong'>Imported Consumers</Box> is the total number
                        of new consumers imported.
                    </Typography>
                    <Typography color='text.secondary'>
                        <Box component='strong'>Updated Consumers</Box> is the number of
                        consumers that already existed in the system and were updated with
                        the details sent through the whitelist file.
                    </Typography>
                    <Typography color='text.secondary'>
                        <Box component='strong'>Ignored Consumers</Box> is the number of
                        consumers that were not imported because their data was broken or
                        invalid.
                    </Typography>
                </Stack>
            </DocSection>

            <DocSection id='whitelist-environments' divider={false} sx={{ pt: 3, pb: 1 }}>
                <Typography variant='h4' gutterBottom>Environments</Typography>
                <Typography color='text.secondary' paragraph>
                    Import or upload a CSV file with whitelisted consumers via API.
                </Typography>
                <Stack spacing={1}>
                    <CodeBlock title='Sandbox URL'>https://testapi.bo.payments-gateway.co</CodeBlock>
                    <CodeBlock title='Production URL'>https://api.bo.payments-gateway.co</CodeBlock>
                </Stack>
                <Alert severity='info' sx={{ mt: 2 }}>
                    Every whitelist API request must include
                    <Box component='code' sx={{ mx: 0.5 }}>auth-id</Box> (API ID) and
                    <Box component='code' sx={{ mx: 0.5 }}>auth-secret</Box> (API GUID).
                    Use <Code>Accept: application/json</Code>, or
                    <Box component='code' sx={{ ml: 0.5 }}>Accept: text/csv</Box> when downloading
                    a CSV stream. File uploads use
                    <Box component='code' sx={{ ml: 0.5 }}>Content-Type: multipart/form-data</Box>.
                </Alert>
            </DocSection>

            <DocSection id='hashed-email-whitelist' divider={false}>
                <Typography variant='h4' gutterBottom>
                    Hashed email whitelist
                </Typography>
                <Typography color='text.secondary' paragraph>
                    If your integration must avoid sending plain-text email addresses in
                    whitelist files, hash each email before creating the CSV. Hashing is a
                    one-way operation and is not encryption; Macropay does not provide a
                    service that hashes or encrypts whitelist data on your behalf.
                </Typography>

                <Alert severity='warning' sx={{ mb: 3 }}>
                    Request the whitelist email hashing secret from Macropay. This is a
                    dedicated secret and is not your API GUID, <Code>auth-secret</Code>,
                    or another merchant API credential. Keep it server-side and do not expose
                    it in browser code, CSV files or source control.
                </Alert>

                <Typography variant='h6' gutterBottom>Hashing algorithm</Typography>
                <Typography color='text.secondary' paragraph>
                    Convert the email to lowercase, append the Macropay-provided
                    <Box component='code' sx={{ mx: 0.5 }}>EMAILS_HASH_SECRET</Box>, calculate
                    SHA-256, and then calculate SHA-256 again over the first hexadecimal hash.
                    Use the same logic in your server-side language if your integration is not
                    written in PHP.
                </Typography>
                <CodeBlock title='PHP example'>{`function getHashedEmail(string $email, string $emailsHashSecret): string
{
    return hash(
        'sha256',
        hash('sha256', strtolower($email) . $emailsHashSecret)
    );
}`}</CodeBlock>

                <SubHeading>CSV format</SubHeading>
                <Typography color='text.secondary' paragraph>
                    Use the columns from the downloaded whitelist template. Phone number is
                    mandatory and must be provided as the fourth CSV column. The optional
                    <Box component='code' sx={{ mx: 0.5 }}>Transaction Count</Box> column is
                    included in both the simplified and EDD whitelist templates. Optional
                    columns must still remain in the CSV file in the template order, but their
                    values can be left empty.
                </Typography>
                <CodeBlock title='CSV example (simplified template)'>{`${whitelistTemplateWithoutSddColumns}
"","","<hashed-email>","<phone>","12"`}</CodeBlock>
                <Alert severity='info' sx={{ mt: 3 }}>
                    Hash the exact email value consistently. The same lowercase email and the
                    same Macropay-provided secret must always produce the same hash so that the
                    consumer can be matched during transaction processing.
                </Alert>
            </DocSection>

            {whitelistEndpoints.map(endpoint => (
                <EndpointSection key={endpoint.id} endpoint={endpoint} />
            ))}
        </DocGroup>

        <DocGroup
            title='Reporting and Payout API'
            description='Authenticated reporting, payout and export operations for your merchant account.'
            sectionIds={groupSectionIds('Reporting and Payout API')}
        >
            <DocSection id='direct-list-parameters' divider={false} sx={{ pt: 3 }}>
                <Typography variant='h4' gutterBottom>List request parameters</Typography>
                <Alert severity='info' sx={{ mb: 3 }}>
                    Every request must include <Code>auth-id</Code> (API ID) and
                    <Box component='code' sx={{ ml: 0.5 }}>auth-secret</Box> (API GUID), using the
                    same base URLs as the whitelist API.
                    Use <Box component='code' sx={{ ml: 0.5 }}>Accept: application/json</Box>, or
                    <Box component='code' sx={{ ml: 0.5 }}>Accept: text/csv</Box> for streamed CSV
                    downloads.
                </Alert>
                <Typography color='text.secondary' paragraph>
                    Routes ending in <Code>/l/i/s/t</Code> accept the parameters below in the
                    request body, either as a form body with
                    <Box component='code' sx={{ mx: 0.5 }}>Content-Type: application/x-www-form-urlencoded</Box>
                    or as JSON with <Box component='code' sx={{ ml: 0.5 }}>Content-Type: application/json</Box>.
                    Replace <Code>column</Code> with any column listed under
                    <Box component='code' sx={{ mx: 0.5 }}>filterable</Box> or
                    <Box component='code' sx={{ ml: 0.5 }}>sortable</Box> in the list response.
                </Typography>

                <Typography variant='h6' sx={{ mb: 1.5 }}>Filtering, sorting and columns</Typography>
                <SimpleTable
                    headers={['Operation', 'Syntax']}
                    rows={listFilterSyntax.map(([operation, syntax]) => [operation, <Code>{syntax}</Code>])}
                />
                <Stack component='ul' spacing={0.5} sx={{ pl: 3, mt: 1.5, mb: 0, color: 'text.secondary' }}>
                    <li><Code>contains</Code> and <Code>notContains</Code> cannot be used alone.</li>
                    <li>
                        Date-time filters use the <Code>Y-m-dTH:i:s</Code> format, for example
                        <Box component='code' sx={{ ml: 0.5 }}>createdAt[from]=2025-06-06T10:00:00</Box>.
                    </li>
                    <li>When <Code>sort[0][dir]</Code> is omitted, the sort direction defaults to <Code>DESC</Code>.</li>
                </Stack>

                <SubHeading>Pagination</SubHeading>
                <SimpleTable
                    headers={['Mode', 'Syntax', 'Behaviour']}
                    rows={listPaginationSyntax.map(([mode, syntax, behaviour]) => [mode, <Code>{syntax}</Code>, behaviour])}
                />
                <Typography color='text.secondary' sx={{ mt: 2 }}>
                    Path values such as <Code>coreId</Code> and <Code>key</Code> are documented
                    on the endpoints that require them.
                </Typography>
            </DocSection>

            {directApiEndpoints.map(endpoint => (
                <EndpointSection key={endpoint.id} endpoint={endpoint} compact />
            ))}
        </DocGroup>
    </>
));

const useDebouncedValue = (value: string, delay: number) => {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timeout = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timeout);
    }, [value, delay]);

    return debounced;
};

/** Publishes the sticky header height as a CSS variable used for sticky and scroll offsets. */
const useStickyHeaderHeight = (rootRef: RefObject<HTMLElement>, headerRef: RefObject<HTMLElement>) => {
    useLayoutEffect(() => {
        const root = rootRef.current;
        const header = headerRef.current;

        if (!root || !header) {
            return undefined;
        }

        const update = () => {
            const isSticky = window.getComputedStyle(header).position === 'sticky';
            root.style.setProperty(DOCUMENTATION_HEADER_HEIGHT_VAR, `${isSticky ? header.offsetHeight : 0}px`);
        };
        const observer = new ResizeObserver(update);

        update();
        observer.observe(header);
        window.addEventListener('resize', update);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [rootRef, headerRef]);
};

const Documentation = (props: ScreenProps) => {
    const [searchInput, setSearchInput] = useState('');
    const [activeTab, setActiveTab] = useState<'documentation' | 'playground'>('documentation');
    const [isContentsOpen, setContentsOpen] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const potentialPaymentMethods = usePotentialPaymentMethods();
    const isProduction = window.location.hostname.toLowerCase() === 'merchants.macropay.net';
    const query = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
    const { matches, scrollToFirstMatch } = useDocumentationSearch(contentRef, query, potentialPaymentMethods.methods);
    const searchState = useMemo(() => ({ matches }), [matches]);

    useStickyHeaderHeight(rootRef, headerRef);

    useEffect(() => {
        if (activeTab !== 'documentation') {
            return undefined;
        }

        const focusSearch = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;
            const isTyping = !!target?.closest('input, textarea, select, [contenteditable="true"]');
            const isShortcut = (event.key === 'k' && (event.ctrlKey || event.metaKey)) || (event.key === '/' && !isTyping);

            if (isShortcut) {
                event.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', focusSearch);
        return () => window.removeEventListener('keydown', focusSearch);
    }, [activeTab]);

    const clearSearch = () => setSearchInput('');

    const printDocumentation = () => {
        clearSearch();
        // Wait for the debounced search to clear so every section is printed.
        window.setTimeout(() => window.print(), SEARCH_DEBOUNCE_MS + 100);
    };

    return (
        <Screen {...props} title='API Documentation'>
            <GlobalStyles styles={{
                [`::highlight(${SEARCH_HIGHLIGHT_NAME})`]: {
                    backgroundColor: '#ffe58f',
                    color: '#101828'
                },
                '@media print': {
                    'body *': { visibility: 'hidden' },
                    '.documentation-print-root, .documentation-print-root *': { visibility: 'visible' },
                    '.documentation-print-root': {
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        background: '#fff'
                    },
                    '.documentation-print-hidden': { display: 'none !important' },
                    '.documentation-print-layout': { display: 'block !important' },
                    '.documentation-print-content': {
                        margin: '0 !important',
                        padding: '20px !important',
                        boxShadow: 'none !important'
                    },
                    '.documentation-code-collapsed': {
                        maxHeight: 'none !important',
                        overflow: 'visible !important'
                    },
                    '.documentation-print-content table, .documentation-print-content pre': {
                        breakInside: 'avoid'
                    }
                }
            }} />
            <Box ref={rootRef} className='documentation-print-root' sx={{ bgcolor: 'background.neutral', minHeight: '100vh' }}>
                <Box
                    ref={headerRef}
                    className='documentation-print-hidden'
                    sx={{
                        // Sticky below the app header on larger screens; on phones it would cover too much of the page.
                        position: { xs: 'static', md: 'sticky' },
                        top: { md: 64, lg: 60 },
                        zIndex: 10,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Container maxWidth='xl'>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent='space-between'
                            spacing={2}
                            sx={{ py: 2 }}
                        >
                            <Typography variant='h5'>Macropay Documentation</Typography>
                            {activeTab === 'documentation' && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                    <TextField
                                        inputRef={searchInputRef}
                                        sx={{ width: { xs: 1, md: 380 } }}
                                        size='small'
                                        value={searchInput}
                                        onChange={event => setSearchInput(event.target.value)}
                                        onKeyDown={event => {
                                            if (event.key === 'Escape') {
                                                clearSearch();
                                            } else if (event.key === 'Enter') {
                                                scrollToFirstMatch();
                                            }
                                        }}
                                        placeholder='Search documentation'
                                        inputProps={{ 'aria-label': 'Search documentation' }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <SearchRounded fontSize='small' />
                                                </InputAdornment>
                                            ),
                                            endAdornment: searchInput ? (
                                                <InputAdornment position='end'>
                                                    <IconButton size='small' onClick={clearSearch} aria-label='Clear search'>
                                                        <CloseRounded fontSize='small' />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : (
                                                <InputAdornment position='end' sx={{ display: { xs: 'none', md: 'flex' } }}>
                                                    <Box
                                                        component='kbd'
                                                        sx={{
                                                            px: 0.75,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                            borderRadius: 0.75,
                                                            fontSize: 12,
                                                            color: 'text.secondary',
                                                            fontFamily: 'inherit'
                                                        }}
                                                    >
                                                        /
                                                    </Box>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                    <Stack direction='row' spacing={1}>
                                        <Button
                                            variant='outlined'
                                            startIcon={<MenuBookRounded />}
                                            onClick={() => setContentsOpen(true)}
                                            sx={{ display: { xs: 'inline-flex', lg: 'none' }, flex: 1 }}
                                        >
                                            Contents
                                        </Button>
                                        <Button
                                            variant='outlined'
                                            startIcon={<PrintRounded />}
                                            onClick={printDocumentation}
                                            sx={{ flex: { xs: 1, sm: 'none' }, whiteSpace: 'nowrap' }}
                                        >
                                            Save as PDF
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                        <Tabs
                            value={activeTab}
                            onChange={(_, value: 'documentation' | 'playground') => {
                                clearSearch();
                                setActiveTab(value);
                            }}
                            aria-label='Documentation sections'
                        >
                            <Tab value='documentation' label='Documentation' />
                            {!isProduction && <Tab value='playground' label='API Playground' />}
                        </Tabs>
                    </Container>
                </Box>

                {activeTab === 'documentation' ? (
                    <DocumentationSearchContext.Provider value={searchState}>
                        <Container maxWidth='xl' disableGutters sx={{ px: { xs: 1, lg: 1.5 } }}>
                            <Box
                                className='documentation-print-layout'
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: '260px minmax(0, 1fr)' },
                                    gap: { xs: 3, lg: 6 },
                                    alignItems: 'start',
                                    position: 'relative'
                                }}
                            >
                                <Box
                                    component='nav'
                                    aria-label='Documentation contents'
                                    className='documentation-print-hidden'
                                    sx={{
                                        display: { xs: 'none', lg: 'block' },
                                        position: 'sticky',
                                        top: DOCUMENTATION_NAV_TOP,
                                        alignSelf: 'start',
                                        zIndex: 2,
                                        py: 2,
                                        maxHeight: `calc(100vh - ${DOCUMENTATION_NAV_TOP} - 12px)`,
                                        overflowY: 'auto',
                                        overscrollBehavior: 'contain',
                                        bgcolor: 'background.neutral'
                                    }}
                                >
                                    <DocumentationNav matches={matches} />
                                </Box>

                                <Paper
                                    ref={contentRef}
                                    className='documentation-print-content'
                                    elevation={0}
                                    sx={{
                                        my: { xs: 2, lg: 4 },
                                        px: { xs: 2, md: 5 },
                                        py: { xs: 2, md: 4 },
                                        minWidth: 0
                                    }}
                                >
                                    {matches && (
                                        <Alert
                                            severity={matches.size ? 'info' : 'warning'}
                                            className='documentation-print-hidden'
                                            sx={{ mb: 3 }}
                                            action={(
                                                <Button color='inherit' size='small' onClick={clearSearch}>
                                                    Clear
                                                </Button>
                                            )}
                                        >
                                            {matches.size
                                                ? `${matches.size} ${matches.size === 1 ? 'section matches' : 'sections match'} “${query.trim()}”. Press Enter to jump to the first result.`
                                                : `No documentation matches “${query.trim()}”.`}
                                        </Alert>
                                    )}
                                    <DocumentationContent
                                        paymentMethods={potentialPaymentMethods.methods}
                                        paymentMethodsLoading={potentialPaymentMethods.isLoading}
                                        paymentMethodsError={potentialPaymentMethods.error}
                                    />
                                </Paper>
                            </Box>
                        </Container>
                        <Drawer
                            anchor='left'
                            open={isContentsOpen}
                            onClose={() => setContentsOpen(false)}
                            PaperProps={{ sx: { width: 300, p: 2 } }}
                        >
                            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
                                <Typography variant='h6'>Contents</Typography>
                                <IconButton onClick={() => setContentsOpen(false)} aria-label='Close contents'>
                                    <CloseRounded />
                                </IconButton>
                            </Stack>
                            <Box component='nav' aria-label='Documentation contents'>
                                <DocumentationNav matches={matches} onNavigate={() => setContentsOpen(false)} />
                            </Box>
                        </Drawer>
                    </DocumentationSearchContext.Provider>
                ) : (
                    <Container maxWidth='xl'>
                        <Paper
                            elevation={0}
                            sx={{
                                my: { xs: 2, lg: 4 },
                                px: { xs: 2, md: 5 },
                                py: { xs: 2, md: 4 }
                            }}
                        >
                            <TransactionPlayground />
                        </Paper>
                    </Container>
                )}
            </Box>
        </Screen>
    );
};

export default Documentation;
